import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Ticket, Trophy, Gift, RotateCcw, Tv, Copy, Check, 
  Sparkles, ArrowLeft, Settings, Music, Hash
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

export default function RaffleHostPage() {
  const { sessionCode: urlSessionCode } = useParams();
  
  const [session, setSession] = useState(null);
  const [hostName, setHostName] = useState('');
  const [firstNumber, setFirstNumber] = useState(1);
  const [lastNumber, setLastNumber] = useState(100);
  const [prizeLabel, setPrizeLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [spinningNumber, setSpinningNumber] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [lastWinner, setLastWinner] = useState(null);
  const spinIntervalRef = useRef(null);

  const displayUrl = session ? `${window.location.origin}/raffle/display/${session.session_code}` : '';

  const fetchSession = useCallback(async (code) => {
    try {
      const res = await fetch(`${API_URL}/api/raffle/sessions/${code}`);
      if (res.ok) {
        const data = await res.json();
        setSession(data);
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  }, []);

  // Check if coming from URL with session code
  useEffect(() => {
    if (urlSessionCode) {
      fetchSession(urlSessionCode);
    }
  }, [urlSessionCode, fetchSession]);

  // Create a new raffle session
  const createSession = async () => {
    if (!hostName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (firstNumber >= lastNumber) {
      toast.error('First number must be less than last number');
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/raffle/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host_name: hostName,
          first_number: parseInt(firstNumber),
          last_number: parseInt(lastNumber)
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSession(data);
        toast.success('Raffle session created!');
        // Update URL without navigation
        window.history.pushState({}, '', `/raffle/host/${data.session_code}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create session');
      }
    } catch (error) {
      toast.error('Failed to create raffle session');
    } finally {
      setCreating(false);
    }
  };

  // Draw a number with spinning animation
  const drawNumber = async () => {
    if (drawing) return;
    
    const label = prizeLabel.trim() || `Draw #${(session?.drawn_numbers?.length || 0) + 1}`;
    
    setDrawing(true);
    setShowResult(false);
    
    // Start spinning animation
    await fetch(`${API_URL}/api/raffle/sessions/${session.session_code}/start-spin`, {
      method: 'POST'
    });

    // Client-side spinning animation
    let spinCount = 0;
    const maxSpins = 25;
    const min = session.first_number;
    const max = session.last_number;
    
    spinIntervalRef.current = setInterval(() => {
      const randomNum = Math.floor(Math.random() * (max - min + 1)) + min;
      setSpinningNumber(randomNum);
      spinCount++;
      
      if (spinCount >= maxSpins) {
        clearInterval(spinIntervalRef.current);
        // Now get the actual result
        finalizeDrawing(label);
      }
    }, 100);
  };

  const finalizeDrawing = async (label) => {
    try {
      const res = await fetch(`${API_URL}/api/raffle/sessions/${session.session_code}/stop-spin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prize_label: label })
      });

      if (res.ok) {
        const data = await res.json();
        setSpinningNumber(data.winning_number);
        setLastWinner({ number: data.winning_number, prize_label: data.prize_label });
        setShowResult(true);
        setPrizeLabel('');
        
        // Refresh session data
        fetchSession(session.session_code);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to draw number');
      }
    } catch (error) {
      toast.error('Failed to complete draw');
    } finally {
      setDrawing(false);
    }
  };

  // Reset the raffle
  const resetRaffle = async () => {
    if (!session) return;
    
    if (!window.confirm('Are you sure you want to reset? All drawn numbers will be cleared.')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/raffle/sessions/${session.session_code}/reset`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        setSession(data.session);
        setSpinningNumber(null);
        setShowResult(false);
        setLastWinner(null);
        toast.success('Raffle reset successfully');
      }
    } catch (error) {
      toast.error('Failed to reset raffle');
    }
  };

  // Update ticket range
  const updateRange = async (newFirst, newLast) => {
    if (!session) return;
    if (newFirst >= newLast) {
      toast.error('First number must be less than last number');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/raffle/sessions/${session.session_code}/update-range`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_number: parseInt(newFirst),
          last_number: parseInt(newLast)
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSession(data);
        toast.success('Ticket range updated');
      }
    } catch (error) {
      toast.error('Failed to update range');
    }
  };

  // Copy display URL
  const copyDisplayUrl = () => {
    navigator.clipboard.writeText(displayUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Display URL copied!');
  };

  // End session
  const endSession = async () => {
    if (!session) return;
    
    if (!window.confirm('Are you sure you want to end this raffle session?')) {
      return;
    }
    
    try {
      await fetch(`${API_URL}/api/raffle/sessions/${session.session_code}`, {
        method: 'DELETE'
      });
      setSession(null);
      setSpinningNumber(null);
      setShowResult(false);
      setLastWinner(null);
      window.history.pushState({}, '', '/raffle/host');
      toast.success('Raffle session ended');
    } catch (error) {
      toast.error('Failed to end session');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (spinIntervalRef.current) {
        clearInterval(spinIntervalRef.current);
      }
    };
  }, []);

  const totalTickets = session ? session.last_number - session.first_number + 1 : 0;
  const drawnCount = session?.drawn_numbers?.length || 0;
  const remainingCount = totalTickets - drawnCount;

  // Setup form - no session yet
  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-900/30 to-slate-950 py-8 px-4">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Ticket className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Raffle Draw</h1>
            <p className="text-slate-400">Draw random ticket numbers</p>
          </div>
          
          {/* Game Type Switcher */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            <Link to="/bingo/host" className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
              <Music className="w-4 h-4" />
              Line Dance
            </Link>
            <Link to="/bingo/uk90/host" className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
              <Hash className="w-4 h-4" />
              UK 90-Ball
            </Link>
            <div className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
              <Ticket className="w-4 h-4" />
              Number Raffle
            </div>
            <Link to="/cloakroom/host" className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
              <Ticket className="w-4 h-4" />
              Cloakroom Tickets
            </Link>
          </div>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Your Name</label>
                <Input
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                  placeholder="Enter your name"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">First Ticket #</label>
                  <Input
                    type="number"
                    value={firstNumber}
                    onChange={(e) => setFirstNumber(parseInt(e.target.value) || 1)}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Last Ticket #</label>
                  <Input
                    type="number"
                    value={lastNumber}
                    onChange={(e) => setLastNumber(parseInt(e.target.value) || 100)}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>

              <Button
                onClick={createSession}
                disabled={creating}
                className="w-full bg-emerald-600 hover:bg-emerald-500 h-12 text-lg"
              >
                {creating ? 'Creating...' : 'Start Raffle Session'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Active raffle session
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900/30 to-slate-950 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/bingo" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Ticket className="w-6 h-6 text-emerald-400" />
                Raffle Draw
              </h1>
              <p className="text-slate-400">Host: {session.host_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-600 text-lg px-3 py-1">
              {session.session_code}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={endSession}
              className="text-slate-400 hover:text-red-400"
            >
              End Session
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Main Draw Area */}
          <div className="lg:col-span-2 space-y-4">
            {/* Display Link */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="bg-white p-2 rounded-lg">
                    <QRCodeSVG value={displayUrl} size={80} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Tv className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-slate-400">TV Display Link</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={copyDisplayUrl}
                        className="text-emerald-400 h-6 px-2"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <div className="text-xs text-slate-400 font-mono truncate">{displayUrl}</div>
                    <a
                      href={displayUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-emerald-400 hover:underline"
                    >
                      Open in new window →
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Number Display */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="relative bg-gradient-to-br from-emerald-900/30 to-slate-800 rounded-xl p-8 text-center min-h-[250px] flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    {drawing ? (
                      <motion.div
                        key="spinning"
                        animate={{ 
                          scale: [1, 1.1, 1],
                        }}
                        transition={{ repeat: Infinity, duration: 0.2 }}
                        className="text-[8rem] font-black text-emerald-400"
                      >
                        {spinningNumber || '?'}
                      </motion.div>
                    ) : showResult && lastWinner ? (
                      <motion.div
                        key="result"
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", damping: 10 }}
                        className="text-center"
                      >
                        <div className="text-lg text-emerald-300 mb-2">{lastWinner.prize_label}</div>
                        <div className="text-[8rem] font-black text-white leading-none">{lastWinner.number}</div>
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <Trophy className="w-12 h-12 text-emerald-400 mx-auto mt-4" />
                        </motion.div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="waiting"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center text-slate-400"
                      >
                        <Gift className="w-24 h-24 mx-auto mb-4 opacity-50" />
                        <p className="text-xl">Ready to draw</p>
                        <p className="text-sm text-slate-400 mt-2">
                          Tickets {session.first_number.toLocaleString()} - {session.last_number.toLocaleString()}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Prize Label Input */}
                <div className="mt-4">
                  <label className="text-sm text-slate-400 mb-1 block">Prize Label (optional)</label>
                  <Input
                    value={prizeLabel}
                    onChange={(e) => setPrizeLabel(e.target.value)}
                    placeholder="e.g., 1st Prize, Star Prize, Hamper"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>

                {/* Draw Button */}
                <Button
                  onClick={drawNumber}
                  disabled={drawing || remainingCount === 0}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 h-14 text-xl mt-4"
                >
                  {drawing ? (
                    <span className="animate-pulse">Drawing...</span>
                  ) : remainingCount === 0 ? (
                    'All Numbers Drawn'
                  ) : (
                    <>
                      <Sparkles className="w-6 h-6 mr-2" />
                      Draw Number
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right: Stats and History */}
          <div className="space-y-4">
            {/* Stats */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-white">{totalTickets.toLocaleString()}</div>
                    <div className="text-xs text-slate-400">Total</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-emerald-400">{remainingCount.toLocaleString()}</div>
                    <div className="text-xs text-slate-400">Remaining</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-amber-400">{drawnCount}</div>
                    <div className="text-xs text-slate-400">Drawn</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Drawn Numbers */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-emerald-400" />
                  Winners ({drawnCount})
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-[300px] overflow-y-auto">
                {drawnCount > 0 ? (
                  <div className="space-y-2">
                    {[...session.drawn_numbers].reverse().map((draw, index) => (
                      <div
                        key={draw.number}
                        className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                          index === 0 ? 'bg-emerald-500/20 border border-emerald-500/50' : 'bg-slate-800/50'
                        }`}
                      >
                        <span className={`font-medium ${index === 0 ? 'text-emerald-300' : 'text-slate-400'}`}>
                          {draw.prize_label}
                        </span>
                        <span className={`text-2xl font-bold ${index === 0 ? 'text-emerald-400' : 'text-white'}`}>
                          {draw.number}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-slate-400 py-8">
                    No numbers drawn yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Controls */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4 space-y-3">
                <Button
                  variant="outline"
                  onClick={resetRaffle}
                  className="w-full border-slate-700 text-slate-300"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset All Draws
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    const newLast = prompt('Enter new last ticket number:', session.last_number);
                    if (newLast && parseInt(newLast) > session.first_number) {
                      updateRange(session.first_number, parseInt(newLast));
                    }
                  }}
                  className="w-full border-slate-700 text-slate-300"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Update Ticket Range
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
